import { EventType } from "@/analytics/analytics-events";
import { EditElementProperties } from "@/analytics/data-organization-events-properties";
import { StartEditWaypointsToolProperties } from "@/analytics/edit-waypoints-tool-properties";
import { AppAwareHtml } from "@/components/r3f/renderers/app-aware-html";
import { useWaypointReference } from "@/components/r3f/utils/placeholder-preview";
import { ElementIconType } from "@/components/ui/icons";
import { SceneContextMenuBase } from "@/components/ui/scene-context-menu";
import { ACTIONS } from "@/components/ui/tree/tree-context-menu/actions";
import { AssignTagsActionContent } from "@/components/ui/tree/tree-context-menu/actions/assign-tags-action";
import { CopyAsLocationMarkerComponent } from "@/components/ui/tree/tree-context-menu/actions/copy-as-location-marker-action";
import { DeleteAction } from "@/components/ui/tree/tree-context-menu/actions/delete-action";
import { MoveWaypointToDifferentArea } from "@/components/ui/tree/tree-context-menu/actions/move-waypoint-action";
import { AddDataAction } from "@/components/ui/tree/tree-context-menu/actions/upload-action";
import {
  MenuDivider,
  selectVisibleContextMenuActions,
} from "@/components/ui/tree/tree-context-menu/tree-context-menu";
import { useCanChangeTool } from "@/hooks/use-can-change-tool";
import { IElementWaypoint, isIElementWaypoint } from "@/hooks/use-placeholders";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { activateTool, ToolName } from "@/store/ui/ui-slice";
import { selectIElementFloorPosition } from "@/utils/camera-transform";
import {
  roundedCalculatePosition,
  TransformOverrides,
} from "@faro-lotv/app-component-toolbox";
import {
  EditWaypointsIcon,
  FaroMenuItem,
  FaroMenuSeparator,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { isIElementRoomSection } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { isEqual } from "es-toolkit";
import { useCallback, useState, type JSX } from "react";
import { shallowEqual } from "react-redux";
import { EditElementNameInput } from "./edit-element-name-input";
import { EditWaypointColor } from "./edit-waypoint-color";

type EditWaypointContextMenuProps = {
  /** The waypoint to show the context menu for */
  waypoint: IElementWaypoint;

  /** Optional transform overrides to apply for the position calculation. */
  transformOverrides?: TransformOverrides;

  /** Callback when the menu should be closed */
  onClose(): void;
};

/**
 * @returns A context menu for quickly editing a waypoint
 */
export function EditWaypointContextMenu({
  waypoint,
  transformOverrides,
  onClose,
}: EditWaypointContextMenuProps): JSX.Element | undefined {
  const dispatch = useAppDispatch();

  // Re-select waypoint from the store so we always have the latest data (e.g. after a name edit)
  const currentWaypoint = useAppSelector(selectIElement(waypoint.id));

  assert(
    currentWaypoint && isIElementWaypoint(currentWaypoint),
    "The selected element must be a waypoint",
  );

  const reference = useWaypointReference(currentWaypoint);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const position = useAppSelector(
    selectIElementFloorPosition(waypoint, transformOverrides),
    isEqual,
  );

  const onEnableEditWaypoints = useCallback(() => {
    Analytics.track<StartEditWaypointsToolProperties>(
      EventType.startEditWaypointsTool,
      {
        via: "waypoint context menu",
      },
    );

    dispatch(activateTool(ToolName.editWaypoints));
    onClose();
  }, [dispatch, onClose]);

  const trackEditElement = useCallback(
    (props: Partial<EditElementProperties>) => {
      assert(reference, "Reference must be defined to track a quick edit");

      Analytics.track<EditElementProperties>(EventType.editElement, {
        via: "waypoint context menu",
        elementType: reference.type,
        elementTypeHint: reference.typeHint ?? undefined,
        hasEditedName: false,
        hasEditedTimePoint: false,
        hasEditedTags: false,
        hasEditedColor: false,
        ...props,
      });
    },
    [reference],
  );

  const trackNameChange = useCallback(() => {
    trackEditElement({ hasEditedName: true });
  }, [trackEditElement]);

  const trackTagsChange = useCallback(() => {
    trackEditElement({ hasEditedTags: true });
  }, [trackEditElement]);

  const trackColorChange = useCallback(() => {
    trackEditElement({ hasEditedColor: true });
  }, [trackEditElement]);

  const canChangeTool = useCanChangeTool();

  const visibleContextMenuActions = useAppSelector(
    selectVisibleContextMenuActions(reference?.id, false),
    shallowEqual,
  );

  if (!reference || !visibleContextMenuActions.length) return;

  return (
    <AppAwareHtml
      position={position}
      calculatePosition={roundedCalculatePosition}
      style={{ pointerEvents: "none" }}
    >
      <div ref={setAnchorEl} />

      {anchorEl && (
        <SceneContextMenuBase
          aria-label="waypoint context menu"
          anchorEl={anchorEl}
          onClose={onClose}
          sx={{
            // We need a fixed width here, to make sure that the various input components don't scale infinitely
            width: 250,
          }}
        >
          {visibleContextMenuActions.map((action, index) => {
            switch (action) {
              case ACTIONS.editWaypointName:
                return (
                  <EditElementNameInput
                    key={index}
                    dark
                    fullWidth
                    element={reference}
                    onNameChanged={trackNameChange}
                  />
                );
              case ACTIONS.enableEditWaypoints:
                return (
                  <FaroMenuItem
                    key={index}
                    Icon={EditWaypointsIcon}
                    label="Move waypoint"
                    secondaryText="Enables edit waypoints mode"
                    onClick={onEnableEditWaypoints}
                    dark
                    disabled={!canChangeTool}
                  />
                );
              case ACTIONS.moveWaypointToOtherArea:
                return (
                  <MoveWaypointToDifferentArea
                    key={index}
                    elementId={reference.id}
                    dark
                  />
                );
              case ACTIONS.copyAsLocationMarker:
                return (
                  <CopyAsLocationMarkerComponent
                    key={index}
                    elementId={reference.id}
                    dark
                    closeContextMenu={onClose}
                  />
                );
              case ACTIONS.assignTags:
                return (
                  <AssignTagsActionContent
                    key={index}
                    elementId={reference.id}
                    label="Assign tag"
                    icon={ElementIconType.TagsAddIcon}
                    dark
                    onTagsChanged={trackTagsChange}
                    closeContextMenu={onClose}
                  />
                );
              case ACTIONS.editWaypointColor:
                assert(
                  isIElementRoomSection(reference),
                  "Only room sections should have the option to edit color",
                );
                return (
                  <EditWaypointColor
                    key={index}
                    waypointReference={reference}
                    onColorChanged={trackColorChange}
                    dark
                  />
                );
              case ACTIONS.addData:
                return (
                  <AddDataAction
                    key={index}
                    elementId={reference.id}
                    dark
                    closeContextMenu={onClose}
                  />
                );
              case ACTIONS.delete:
                return (
                  <DeleteAction
                    key={index}
                    elementId={reference.id}
                    dark
                    closeContextMenu={onClose}
                  />
                );
              case MenuDivider:
                return <FaroMenuSeparator key={index} dark />;
              default:
                return null;
            }
          })}
        </SceneContextMenuBase>
      )}
    </AppAwareHtml>
  );
}
